import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import "./quiz.css";
import { API_BASE_URL } from "../config";

export default function QuizPage() {
  /* ================= STATES ================= */
  const [step, setStep] = useState("form"); // form | quiz | result
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [timeLeft, setTimeLeft] = useState(30);

  const [answers, setAnswers] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [user, setUser] = useState({
    name: "",
    phone: "",
    email: "",
    department: "",
    year: "",
    college: "",
  });

  const [errors, setErrors] = useState({});
  const [result, setResult] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [banner, setBanner] = useState(null);
  const [showPopup, setShowPopup] = useState(true);

  const [quizLive, setQuizLive] = useState(false);
  const [loading, setLoading] = useState(true);

  /* ================= INITIAL LOAD ================= */
  useEffect(() => {
    fetchBanner();
    fetchLeaderboard();
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setShowPopup(false), 6000);
    return () => clearTimeout(t);
  }, []);

  /* ================= BLOCK BACK / REFRESH ================= */
  useEffect(() => {
    if (step !== "quiz") return;

    const blockBack = () => {
      window.history.pushState(null, "", window.location.href);
    };

    window.history.pushState(null, "", window.location.href);
    window.addEventListener("popstate", blockBack);

    const handleUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };

    window.addEventListener("beforeunload", handleUnload);

    return () => {
      window.removeEventListener("popstate", blockBack);
      window.removeEventListener("beforeunload", handleUnload);
    };
  }, [step]);

  useEffect(() => {
    fetch(`${API_BASE_URL}/api/quiz/status`)
      .then((res) => res.json())
      .then((data) => {
        setQuizLive(data.quizLive);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  /* ================= API ================= */
  const fetchBanner = async () => {
    try {
      const res = await axios.get(`${API_BASE_URL}/api/quiz/banner`);
      setBanner(`${API_BASE_URL}${res.data.image}`);
    } catch {
      setBanner(null);
    }
  };

  const fetchLeaderboard = async () => {
    const res = await axios.get(`${API_BASE_URL}/api/quiz/leaderboard`);
    setLeaderboard(res.data);
  };

  /* ================= VALIDATION ================= */
  const validateForm = () => {
    const e = {};

    if (!user.name.trim()) e.name = "Name required";
    if (!/^[6-9]\d{9}$/.test(user.phone)) e.phone = "Valid phone required";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(user.email))
      e.email = "Valid email required";
    if (!user.department) e.department = "Department required";
    if (!/^[1-6]$/.test(user.year)) e.year = "Year must be 1–6";
    if (!user.college) e.college = "College required";

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ================= START QUIZ ================= */
  const handleStartQuiz = async (e) => {
    e.preventDefault();

    if (!quizLive) {
      alert("Quiz is currently closed. Please come back between 1st–20th.");
      return;
    }

    if (!validateForm()) return;

    try {
      await axios.post(`${API_BASE_URL}/api/quiz/check`, {
        email: user.email,
        phone: user.phone,
      });

      const res = await axios.get(`${API_BASE_URL}/api/quiz/random?count=25`);

      setQuestions(res.data.questions);
      setStep("quiz");
      setCurrentIndex(0);
      setTimeLeft(30);
      setAnswers({});
    } catch (err) {
      alert(
        err.response?.data?.message || "You have already attended this quiz"
      );
    }
  };

  /* ================= ANSWER SELECT ================= */
  const handleSelect = (qid, idx) => {
    setAnswers((prev) => ({ ...prev, [qid]: idx }));
  };

  /* ================= FINAL SUBMIT ================= */
  const submitFinalQuiz = useCallback(async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      const res = await axios.post(`${API_BASE_URL}/api/quiz/submit`, {
        ...user,
        answers: questions.map((q) => ({
          questionId: q._id,
          selectedIndex: answers[q._id],
        })),
      });

      setResult(res.data);
      setStep("result");
      fetchLeaderboard();
    } catch (err) {
      alert(err.response?.data?.message || "Submission failed");
      setStep("form");
    } finally {
      setIsSubmitting(false);
    }
  }, [answers, isSubmitting, questions, user]);

  /* ================= NEXT QUESTION ================= */
  const handleNext = useCallback(() => {
    const q = questions[currentIndex];

    if (answers[q._id] === undefined) {
      setAnswers((p) => ({ ...p, [q._id]: -1 }));
    }

    setTimeLeft(30);

    if (currentIndex === questions.length - 1) {
      submitFinalQuiz();
    } else {
      setCurrentIndex((i) => i + 1);
    }
  }, [answers, currentIndex, questions, submitFinalQuiz]);

  /* ================= TIMER ================= */
  useEffect(() => {
    if (step !== "quiz") return;

    if (timeLeft === 0) {
      handleNext();
      return;
    }

    const timer = setTimeout(() => setTimeLeft((t) => t - 1), 1000);
    return () => clearTimeout(timer);
  }, [timeLeft, step, handleNext]);

  if (loading) {
    return <p className="quiz-loading">Loading quiz...</p>;
  }

  if (!quizLive) {
    return (
      <div className="quiz-wrapper">
        <div className="quiz-box quiz-closed">
          <h2>🚫 Quiz Closed</h2>
          <p>
            The quiz runs from <b>1st to 20th</b> of every month.
          </p>
          <p>We are currently analyzing results and announcing winners 🎉</p>
          <p>
            Please check back on the <b>1st</b>.
          </p>
        </div>
      </div>
    );
  }

  /* ================= UI ================= */
  return (
    <div
      className="quiz-wrapper"
      style={{ backgroundImage: banner ? `url(${banner})` : "none" }}
    >
      {showPopup && step === "form" && (
        <div className="quiz-end-popup">
          <div className="popup-card">
            <span className="close" onClick={() => setShowPopup(false)}>
              ×
            </span>
            <h3>🚀 Drone Quiz</h3>
            <p>
              Quiz will end on <b>20th</b>.<br />
              Don’t miss your chance!
            </p>
          </div>
        </div>
      )}

      <div className="quiz-box">
        <h2 className="quiz-title">Drone Knowledge Quiz</h2>
        {/* ---------- LEADERBOARD (ONLY ON ENTRY) ---------- */}
        {step === "form" && leaderboard.length > 0 && (
          <div className="leaderboard">
            <h3>🏆 Top Performers</h3>
            {leaderboard.slice(0, 3).map((l, i) => (
              <p key={i}>
                #{i + 1} {l.name} – {l.college}
              </p>
            ))}
          </div>
        )}

        {/* ================= FORM ================= */}
        {step === "form" && (
          <form onSubmit={handleStartQuiz} className="quiz-form">
            {Object.keys(user).map((k) => (
              <div key={k}>
                <input
                  placeholder={k.toUpperCase()}
                  value={user[k]}
                  onChange={(e) => setUser({ ...user, [k]: e.target.value })}
                />
                {errors[k] && <p className="error">{errors[k]}</p>}
              </div>
            ))}
            <button type="submit" disabled={!quizLive}>
              {quizLive ? "Start Quiz" : "Quiz Closed"}
            </button>
          </form>
        )}

        {/* ================= QUIZ ================= */}
        {step === "quiz" && questions.length > 0 && (
          <div className="question-box">
            <div className="quiz-header">
              <span>
                Question {currentIndex + 1} / {questions.length}
              </span>
              <span className="timer">⏱ {timeLeft}s</span>
            </div>

            <p className="question-text">{questions[currentIndex].text}</p>

            {questions[currentIndex].choices.map((c, idx) => (
              <label key={idx} className="option">
                <input
                  type="radio"
                  checked={answers[questions[currentIndex]._id] === idx}
                  onChange={() =>
                    handleSelect(questions[currentIndex]._id, idx)
                  }
                />
                {c}
              </label>
            ))}

            <button onClick={handleNext}>
              {currentIndex === questions.length - 1 ? "Submit Quiz" : "Next"}
            </button>
          </div>
        )}

        {/* ================= RESULT ================= */}
        {step === "result" && result && (
          <div className="score-box">
            <h3>
              🎉 Score: {result.totalScore} / {result.totalMaxScore}
            </h3>
          </div>
        )}
      </div>
    </div>
  );
}
